/**
 * CASTLE ATLAS — Copywriting Landing Page
 * ═══════════════════════════════════════════════════
 * Changes in this pass (post local-test fixes):
 *   1. Sheet paths fixed — the PNGs live flat at frontend/public/copywriting/,
 *      not frontend/public/copywriting/modules/. Fixed all 5 references.
 *   2. Module grid and Value Strip grid switched from a fixed
 *      isMobile-conditional repeat(3,1fr)/1fr to
 *      repeat(auto-fit, minmax(Npx, 1fr)). This was also the cause of the
 *      layout breaking at in-between widths: 3 columns were being forced
 *      into a container too narrow for them, so card footer text overflowed
 *      into the row below. auto-fit/minmax removes the hard breakpoint
 *      entirely — the browser picks the column count from the min width.
 *   3. PinnedNote hover shadow direction reversed — hover now REDUCES the
 *      shadow (half the blur/spread/opacity of the base state) instead of
 *      increasing it.
 *   4. Skill Radar fully rebuilt. Recharts is gone from this file — it was
 *      sizing the chart by the smaller of width/height, and this panel is
 *      full-width but short, so it kept drawing a tiny pentagon stranded in
 *      empty space no matter how the container was styled. Replaced with a
 *      hand-drawn SVG radar (same jittered-line technique as the buttons/
 *      card borders elsewhere on this page), laid out two-column (label +
 *      legend left, chart right) instead of one full-width strip. "Skill
 *      Radar" label kept; "What mastery looks like" reworked into "A rough
 *      sketch of your range" plus a supporting line.
 *
 * Changes in THIS pass (new sheet art + hero desk + radar + closing band):
 *   5. New 1068×1500 sheet art dropped in for all 5 modules (same file
 *      names, same /copywriting/ path, so no path changes needed). Module
 *      tiles and the Radar tile now share a MODULE_ASPECT ratio computed
 *      from the sheet's real proportions instead of a hardcoded 1/1 square,
 *      so the paper fills its tile edge-to-edge instead of floating in a
 *      letterboxed square with dead space left/right.
 *   6. Skill Radar: grid-ring/spoke/benchmark opacity and stroke width
 *      raised across the board — at strokeWidth 1 inside a 340-unit viewBox
 *      the rings were rendering close to hairline once scaled into the
 *      tile, which is why it read as an empty square with a dot instead of
 *      a pentagon. Ghost (0-rep) state now also draws a faint low-value
 *      data polygon instead of only a center dot, so the "spider" shape is
 *      visible immediately, matching the 2a reference.
 *   7. Hero rebuilt as a two-column grid (text left, a new HeroDeskVisual
 *      right) on wide viewports, collapsing to a single stacked column
 *      under 900px. HeroDeskVisual holds the new hero sheet/pen/paperclip
 *      photo plus a text overlay ("Write" / "Rewrite" struck through,
 *      "Convert." circled in a hand-drawn purple ring) using the same
 *      strike-through technique as HeroCrossout and the closing headline.
 *   8. Progress band (c6) split: the pencil progress ring now sits inside
 *      its own small card with a gently wobbly (sk()-drawn) hand-drawn
 *      border — not a jagged/sawtooth edge — with the 3-step flow
 *      ("Read the brief → Write the copy → Get scored") printed underneath
 *      it, mirroring the 2a reference's inline step breadcrumb.
 *   9. Closing CTA (c7) switched from plain paper background to the same
 *      light purple gradient as the band, so the page's last section reads
 *      as a soft purple close rather than a flat cream box (and never the
 *      dark near-black block from the earlier reference mockup).
 *
 * Changes in THIS pass (responsiveness system fixes — Aug 4):
 *  10. Skill Radar: removed the flat `transform: scale(1.75)` used to make
 *      the chart "75% bigger." Transform-scale doesn't participate in
 *      layout or in the aspect-ratio math of its containing box, and this
 *      tile also carries `containerType: inline-size` (needed for cqw),
 *      which establishes containment on the box — so at narrow widths the
 *      scaled-up paint was getting clipped by its own container instead of
 *      growing with it, which read as the chart "shrinking" and its axis
 *      labels bleeding off the edge on mobile. The "bigger" request is now
 *      met by cropping the viewBox tighter around the actual chart (bigger
 *      radius relative to the canvas, tighter label margin) so the same
 *      box shows a visually larger chart at every width, instead of
 *      painting past the box's own edge.
 *  11. Hero section: replaced the free-floating `position: absolute`
 *      HeroDeskVisual (sized independently off `46vw`, with a single hard
 *      `isMobile` cutoff at 640px) with a real two-column CSS grid. Below
 *      640px there was no gap between "mobile stacked" and "desktop with
 *      room" — from 640px up to ~1400px the absolutely-positioned image
 *      had no reserved space and sat directly on top of the headline text,
 *      which is the "converging in the header" overlap. The text column
 *      now always owns its own real grid track; only the image is allowed
 *      to bleed outside its track (matching the "bleed past the edge"
 *      direction), and the stacked-mobile layout now kicks in at 900px,
 *      matching what this file's own comments already said it should do.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '@hooks/usePageTitle';
import { useProgress } from '@hooks/useProgress';
import { useForceSurface } from '@hooks/useSurface';
import {
  ArrowRight, PenTool, Layers, Sparkles,
  Zap, BookOpen,
} from 'lucide-react';
import { ALL_COPYWRITING_EXERCISES, getExercisesByModule } from '../data/copywritingExercises';



/* ─── TOKENS ─── */
const T = {
  radius: { lg: 14, xl: 18 },
  trans: 'all 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
  font: {
    sans: 'var(--font-body)',
    serif: 'var(--font-skill-copy)',
    mono: 'var(--font-mono)',
    script: 'var(--font-script, "Caveat", cursive)',
  },
};

const COLORS = {
  purple: '#7c3aed', paper: '#fefce8', ink: '#2d2008',
  redPen: '#dc2626',
  band: {
    bg: 'linear-gradient(145deg, #faf5ff 0%, #ede9fe 60%, #ddd6fe 100%)',
    border: '#c4b5fd',
    head: '#4c1d95',
    body: '#6d28d9',
  },
};

// Letterpress-style "pressed into the paper" text shadow — dark shadow above,
// light catch-light below — used on every module title treatment.
const EMBOSS = '0 1px 1px rgba(255,255,255,0.55), 0 -1px 1px rgba(45,32,8,0.35)';

// Real proportions of the sheet art (1068×1500 px). Module tiles and the
// Radar tile share this instead of a hardcoded square, so the paper fills
// its tile instead of floating letterboxed inside one.
const MODULE_ASPECT = '1068 / 1500';

// Module grid sizing — named so every tile wrapper (module cards + the
// radar tile) pulls from the same numbers instead of drifting apart.
const MODULE_GRID_MIN = 320;
const MODULE_GRID_MAX = 440;
const MODULE_GRID_GAP = 24;

// Hero desk visual — drop the finished composite (pen, crumpled paper,
// paperclips, wax stamp) at this path. See the chat notes on filename.
const HERO_DESK_SHEET = '/copywriting/hero-the-desk.webp';

/* ─── SKETCH HELPER (decorative borders only) ─── */
const sk = (x, y, w, h, s) => {
  const j = (n, k) => n + ((k * 7 + 3) % 5) - 2;
  const [x1, y1] = [j(x, s), j(y, s + 1)];
  const [x2, y2] = [j(x + w, s + 2), j(y, s + 3)];
  const [x3, y3] = [j(x + w, s + 4), j(y + h, s + 5)];
  const [x4, y4] = [j(x, s + 6), j(y + h, s + 7)];
  return `M${x1},${y1} Q${(x1 + x2) / 2},${y1 + ((s * 11) % 4) - 2} ${x2},${y2} Q${x3 + 1},${(y2 + y3) / 2} ${x3},${y3} Q${(x3 + x4) / 2},${y3 + 2} ${x4},${y4} Q${x1 - 2},${(y4 + y1) / 2} ${x1},${y1} Z`;
};

/* ─── MODULE DATA with centralized slugs + the real sheet art ───
   Sheet paths point at frontend/public/copywriting/ — a flat public/
   folder, so these are plain string paths, not JS imports.
   Only fields actually read anywhere are kept: desc, badge, and rotation
   were left over from an earlier card design (title+description+status
   pill) that's since been stripped down to just title+CTA — nothing in
   ModuleCard or ModuleTitle reads any of the three, so they were dead
   data, not dead code with no effect. */
const MODULES = [
  {
    num: '01', title: 'Headline Duel', cta: 'Enter the ring',
    style: 'boxing', slug: 'headline-duel',
    sheet: '/copywriting/headline-duel.webp',
  },
  {
    num: '02', title: 'The Living Ad', cta: 'Make the ad',
    style: 'magazine', slug: 'living-ad',
    sheet: '/copywriting/the-living-ad.webp',
  },
  {
    num: '03', title: 'The Swipe File', cta: 'Open the file',
    style: 'folder', slug: 'swipe-file',
    sheet: '/copywriting/the-swipe-file.webp',
  },
  {
    num: '04', title: 'The Brief', cta: 'Accept the brief',
    style: 'formal', slug: 'brief',
    sheet: '/copywriting/the-brief.webp',
  },
  {
    num: '05', title: 'A/B Arena', cta: 'Test your gut',
    style: 'split', slug: 'ab-arena',
    sheet: '/copywriting/ab-arena.webp',
  },
];
const SLUG_MAP = Object.fromEntries(MODULES.map(m => [m.num, m.slug]));

const RADAR_AXES = ['Headlines', 'Ad Copy', 'Swipe Analysis', 'Client Briefs', 'A/B Testing'];
const MODULE_SKILL_MAP = {
  'headline-duel': 'Headlines',
  'living-ad': 'Ad Copy',
  'swipe-file': 'Swipe Analysis',
  'brief': 'Client Briefs',
  'ab-arena': 'A/B Testing',
};

const HOVER_TILTS = [
  'translateY(-5px) rotate(-1.5deg)',
  'translateY(-5px) rotate(1.5deg)',
  'translateY(-5px) rotate(-1deg)',
  'translateY(-5px) rotate(2deg)',
  'translateY(-5px) rotate(-0.5deg)',
];

const FRAMEWORKS = [
  { name: 'AIDA', desc: 'Attention · Interest · Desire · Action', example: 'Stop scrolling. Here is why. You need this. Buy now.' },
  { name: 'PAS', desc: 'Problem · Agitate · Solution', example: 'Your ads fail. Every day you lose money. Here is the fix.' },
  { name: 'FAB', desc: 'Features · Advantages · Benefits', example: 'Steel frame. Lasts 10 years. Sleep sound every night.' },
  { name: 'Slippery Slope', desc: 'Sugarman', example: 'One sentence pulls you to the next. You cannot stop reading until you reach the end.' },
  { name: 'The Control', desc: 'Halbert', example: 'Find what works. Beat it by 10%. Repeat forever.' },
  { name: 'The 4 Us', desc: 'Urgent · Unique · Ultra-specific · Useful', example: 'Only 3 left. One of a kind. 47% faster. Saves 2 hours daily.' },
  { name: 'BAB', desc: 'Before · After · Bridge', example: 'Tired. Energized. This course gets you there.' },
  { name: 'Ogilvy Rules', desc: 'Headline Rules', example: 'The headline is 80 cents of your dollar. Make it count.' },
];

const HOW_STEPS = [
  { num: '01', title: 'READ THE BRIEF', desc: 'You get a real brief. A product. A target audience. A goal. No hand-holding. This is the real thing.' },
  { num: '02', title: 'WRITE THE COPY', desc: 'Text box. Timer optional. AI watches. Write your headline, email, ad, or CTA here.' },
  { num: '03', title: 'GET SCORED', desc: 'Not just a number. A breakdown. Hook strength · Clarity · Persuasion · CTA quality · Originality.' },
];

const HeroCrossout = () => (
  <h1 style={{
    fontFamily: T.font.serif, fontWeight: 900,
    fontSize: 'clamp(2.1rem, 5.2vw, 3.4rem)', lineHeight: 1.05,
    letterSpacing: '-0.02em', margin: 0,
  }}>
    <span style={{
      display: 'inline-block', transform: 'rotate(-2deg)',
      textDecoration: 'line-through', color: '#c9bfae',
    }}>Good Great Perfect</span>
    <br />
    <span style={{ color: COLORS.purple }}>Copy That Converts.</span>
  </h1>
);

/* ─── HERO DESK VISUAL — the pen / crumpled-paper / paperclips / wax-stamp
   photo, propped at a slight tilt like it's sitting on a desk. The
   "Write"/"Rewrite"/"Convert." messaging is now baked into the artwork
   itself, so this no longer draws a DOM text overlay on top of it — see
   the drop-in note in the chat reply for path/filename. ─── */
const HeroDeskVisual = () => (
  <div style={{ position: 'relative', width: '100%', transform: 'rotate(2.5deg)', containerType: 'inline-size' }}>
    <img src={HERO_DESK_SHEET} alt="" loading="eager" decoding="async" fetchPriority="high" draggable={false} style={{
      display: 'block', width: '100%', height: 'auto',
      filter: 'drop-shadow(0 24px 40px rgba(45,32,8,0.22))',
      userSelect: 'none', pointerEvents: 'none',
    }} />
  </div>
);

const PencilProgressRing = ({ pct, size = 90, color = COLORS.purple }) => {
  const r = size / 2;
  const numPoints = 90;
  const innerR = r - 9;
  const wobble = 0.6;
  const generatePath = () => Array.from({ length: numPoints + 1 }, (_, i) => {
    const angle = (i / numPoints) * Math.PI * 2 - Math.PI / 2;
    const noise = Math.sin(i * 1.7 + 0.6) * wobble;
    const rad = innerR + noise;
    return `${i === 0 ? 'M' : 'L'}${(r + Math.cos(angle) * rad).toFixed(2)},${(r + Math.sin(angle) * rad).toFixed(2)}`;
  }).join(' ') + 'Z';
  const approxCirc = 2 * Math.PI * innerR;
  const dashFill = (pct / 100) * approxCirc;
  const ink = COLORS.ink;
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <path d={generatePath()} fill="none" stroke="rgba(45,32,8,0.08)" strokeWidth="2.5" strokeLinecap="round" />
        <path d={generatePath()} fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round"
          strokeDasharray={`${dashFill} ${approxCirc * 1.5}`} opacity={0.9}
          style={{ transition: 'stroke-dasharray 1.2s cubic-bezier(0.22,1,0.36,1)' }} />
        <text x={r} y={r - 3} textAnchor="middle" fontSize="15" fontWeight="900" fill={ink} fontFamily="Georgia, serif">{pct}%</text>
        <text x={r} y={r + 12} textAnchor="middle" fontSize="7" fontWeight="700"
          fill="rgba(45,32,8,0.4)"
          fontFamily="system-ui, sans-serif" letterSpacing="0.1em">DRAFTED</text>
      </svg>
    </div>
  );
};

/* ─── PROGRESS CARD — the pencil ring in its own little box, with the
   3-step flow printed underneath it. Border uses the same sk() jitter
   helper as SketchyButton — a gentle, hand-drawn wobble, not the sharp
   torn-paper/sawtooth edge used on the sheet photos elsewhere on the page. ─── */
const ProgressCard = ({ pct, size, copyProgress, totalExercises }) => {
  const W = 280, H = 196;
  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: 280, height: H }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        <path d={sk(4, 4, W - 8, H - 8, 24)} fill="rgba(255,255,255,0.45)"
          stroke={COLORS.purple} strokeWidth={1.6} opacity={0.6} />
      </svg>
      <div style={{
        position: 'relative', height: '100%', padding: '16px 18px',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: 6, textAlign: 'center',
      }}>
        <PencilProgressRing pct={pct} size={size} color={COLORS.purple} />
        <p style={{ fontSize: 12, color: COLORS.band.body, margin: '2px 0 0' }}>
          {copyProgress} of {totalExercises} exercises
        </p>
        <p style={{
          fontSize: 10.5, color: COLORS.band.head, fontFamily: T.font.mono,
          letterSpacing: '0.01em', margin: 0,
        }}>
          Read the brief <span style={{ color: COLORS.purple }}>→</span> Write the copy <span style={{ color: COLORS.purple }}>→</span> Get scored
        </p>
      </div>
    </div>
  );
};

/* ─── SKILL RADAR — a center hub (purple), five thin spokes running out to
   each skill, and a progress dot per spoke that moves outward AND grows as
   reps get logged for that skill. No connecting polygon between the dots —
   each spoke reads independently, "five spikes in five directions." Dots
   are the same purple as the hub (no separate accent color), have no
   stroke outline, and sit for real at frac=0 (i.e. exactly on the hub)
   when a skill has zero reps.
   Sizing (Aug 4 pass): the "75% bigger" request used to be a flat
   `transform: scale(1.75)` on the whole widget. Transform-scale paints
   outside the box without telling the box's own aspect-ratio/containment
   math it did that, so at narrow widths the paint was clipped by its own
   container — the chart looked like it was "shrinking" and axis labels
   bled off the tile edge on mobile. Fixed here by cropping the viewBox
   tighter around the actual chart instead (bigger r relative to the
   canvas, tighter label margin) — same idea as before, but the "bigger"
   lives inside the box instead of painted past it, so it can never be
   clipped and it scales in lockstep with its container at every width. ─── */
const radarVertex = (index, frac, cx, cy, r) => {
  const angle = -Math.PI / 2 + index * ((2 * Math.PI) / 5);
  const rad = r * frac;
  return [cx + Math.cos(angle) * rad, cy + Math.sin(angle) * rad, angle];
};

const radarPath = (points) => {
  const [first, ...rest] = points;
  return `M${first[0].toFixed(1)},${first[1].toFixed(1)} ` +
    rest.map(p => `L${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ') + ' Z';
};

const SketchRadar = ({ radarData, isGhost }) => {
  // Canvas cropped down from the old 400×360/r=100 to 300×272/r=100 —
  // same spoke length, tighter frame around it, so the chart itself reads
  // ~30% bigger inside an identically-sized box with no risk of clipping.
  const cx = 150, cy = 140, r = 100;
  const labelFrac = 1.22;

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <svg viewBox="0 0 300 272" style={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}>
        <path d={radarPath(radarData.map((_, i) => radarVertex(i, 1, cx, cy, r)))}
          fill="none" stroke="rgba(45,32,8,0.07)" strokeWidth={1} />
        {radarData.map((_, i) => {
          const [vx, vy] = radarVertex(i, 1, cx, cy, r);
          return <line key={i} x1={cx} y1={cy} x2={vx} y2={vy} stroke="rgba(45,32,8,0.2)" strokeWidth={1.3} />;
        })}
        <circle cx={cx} cy={cy} r={15} fill={COLORS.purple} opacity={0.13} />
        <circle cx={cx} cy={cy} r={6} fill={COLORS.purple} />
        {radarData.map((d, i) => {
          const frac = isGhost ? 0 : d.value / 100;
          const [px, py] = radarVertex(i, frac, cx, cy, r);
          const dotR = isGhost ? 4 : 4 + (d.value / 100) * 4;
          const dotTransition = { transition: 'cx 0.6s cubic-bezier(0.22,1,0.36,1), cy 0.6s cubic-bezier(0.22,1,0.36,1), r 0.6s ease' };
          return (
            <g key={i}>
              {/* soft glow halo, same technique as the center hub's */}
              <circle cx={px} cy={py} r={dotR + 6} fill={COLORS.purple} opacity={0.16} style={dotTransition} />
              <circle cx={px} cy={py} r={dotR} fill={COLORS.purple} style={dotTransition} />
            </g>
          );
        })}
        {radarData.map((d, i) => {
          const [lx, ly, angle] = radarVertex(i, labelFrac, cx, cy, r);
          const cosA = Math.cos(angle);
          const anchor = Math.abs(cosA) < 0.15 ? 'middle' : (cosA > 0 ? 'start' : 'end');
          return (
            <text key={i} x={lx} y={ly} textAnchor={anchor}
              fontFamily={T.font.mono} fontSize="10.5" fontWeight="700"
              fill={COLORS.band.head} letterSpacing="0.03em">
              {d.skill.toUpperCase()}
            </text>
          );
        })}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 10 }}>
        <span style={{ width: 8, height: 8, borderRadius: 99, background: COLORS.purple, display: 'inline-block' }} />
        <span style={{ fontSize: 11, color: 'rgba(45,32,8,0.55)' }}>Your progress in each skill</span>
      </div>
    </div>
  );
};

/* ─── RADAR TILE — the Skill Radar as the 6th tile inside the modules
   grid. No box, no border, no background, no label — just the chart.
   Same aspect-ratio/grid footprint as a module card ("feel like the
   others"), same containerType as a module card too, so if its own
   cqw-sized elements are ever added they behave identically to the rest
   of the grid — no bolted-on scale transform, no separate sizing system. ─── */
const RadarTile = ({ radarData, isGhost }) => (
  <div style={{
    position: 'relative', aspectRatio: MODULE_ASPECT, containerType: 'inline-size',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6cqw 3cqw',
  }}>
    <SketchRadar radarData={radarData} isGhost={isGhost} />
  </div>
);

// Font sizes below are in cqw (container-query width units) instead of
// px — every ModuleCard root now has containerType:'inline-size' set, so
// 1cqw = 1% of that card's own rendered width. That's what "parented to
// the image" means in practice: the card is the sheet photo, so text sized
// off the card scales exactly with the sheet photo, at any viewport width
// or zoom level, instead of holding a fixed px size independent of it.
const ModuleCardFooter = ({ cta }) => (
  <div style={{ marginTop: 'auto' }}>
    <div style={{
      height: 1, marginBottom: '2.6cqw',
      background: 'linear-gradient(to right, rgba(45,32,8,0.32), transparent 55%)',
    }} />
    <span style={{
      fontSize: '5.5cqw', fontWeight: 700, color: COLORS.purple,
      fontFamily: T.font.script, display: 'flex', alignItems: 'center', gap: '1.6cqw',
    }}>{cta} <ArrowRight size={16} /></span>
  </div>
);

const ModuleTitle = ({ module }) => {
  const { style: modStyle, title } = module;

  if (modStyle === 'boxing') {
    // "Duel": the same word facing its own mirror image (literal
    // transform: scaleX(-1) flip, purple-tinted so it reads as the rival),
    // with a small cross badge — same red/shadow treatment as A/B Arena's
    // VS badge — sitting where the two would clash.
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', transform: 'translateY(-70px)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.6cqw', fontSize: '3.2cqw', fontWeight: 700, color: 'rgba(45,32,8,0.5)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '2.6cqw' }}>
          <svg viewBox="0 0 12 12" style={{ width: '3.2cqw', height: '3.2cqw', flexShrink: 0 }}>
            <line x1="1.5" y1="1.5" x2="10.5" y2="10.5" stroke={COLORS.redPen} strokeWidth="1.6" strokeLinecap="round" />
            <line x1="10.5" y1="1.5" x2="1.5" y2="10.5" stroke={COLORS.redPen} strokeWidth="1.6" strokeLinecap="round" />
          </svg>
          Headline
        </div>
        <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <span style={{ fontFamily: 'Georgia, serif', fontSize: '10cqw', fontWeight: 900, color: COLORS.ink, lineHeight: 1, textShadow: EMBOSS }}>DUEL</span>
          <span style={{ width: '6.8cqw', flexShrink: 0 }} />
          <span style={{ fontFamily: 'Georgia, serif', fontSize: '10cqw', fontWeight: 900, color: COLORS.purple, lineHeight: 1, transform: 'scaleX(-1)', opacity: 0.8, textShadow: EMBOSS }}>DUEL</span>
          <span style={{
            position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%) rotate(-8deg)',
            width: '8cqw', height: '8cqw', borderRadius: '50%',
            background: COLORS.redPen, display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.35), inset 0 -2px 3px rgba(0,0,0,0.25), 0 4px 10px rgba(0,0,0,0.3)',
            zIndex: 2,
          }}>
            <svg viewBox="0 0 16 16" style={{ width: '4.2cqw', height: '4.2cqw' }}>
              <line x1="2" y1="2" x2="14" y2="14" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
              <line x1="14" y1="2" x2="2" y2="14" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
            </svg>
          </span>
        </div>
      </div>
    );
  }

if (modStyle === 'magazine') {
  return (
    <div style={{ position: 'relative', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {/* LIVING watermark – bottom‑left to top‑right diagonal */}
<h3 style={{
  position: 'absolute',
  left: 55,
  bottom: 35,
  transform: 'rotate(-51deg)',
  transformOrigin: 'bottom left',
  fontFamily: 'Georgia, serif',
  fontSize: '24.7cqw',
  fontWeight: 900,
  color: COLORS.ink,
  letterSpacing: '-0.01em',
  lineHeight: 0.95,
  margin: 0,
  whiteSpace: 'nowrap',
  opacity: 0.05,
  animation: 'livingPulseOpacity 3s ease-in-out infinite', // 👈 this is new
}}>LIVING</h3>

      {/* AD stamp – centered */}
      <span style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%) rotate(-24deg)',
        fontSize: '4.2cqw',
        fontWeight: 800,
        color: COLORS.purple,
        border: `2px solid ${COLORS.purple}`,
        borderRadius: 5,
        padding: '0.8cqw 2.2cqw',
        display: 'inline-block',
        background: 'transparent',
      }}>AD</span>
    </div>
  );
}

  if (modStyle === 'folder') {
    return (
      <div style={{
        alignSelf: 'center', marginTop: 'calc(38% + 40px)',
        fontFamily: 'Georgia, serif', fontSize: '6.3cqw', fontWeight: 900,
        color: '#9a3412', letterSpacing: '0.03em', textTransform: 'uppercase',
        border: '4px solid #9a3412', borderRadius: 6,
        padding: '1.8cqw 3.9cqw', transform: 'rotate(-3deg)', opacity: 0.85,
        textShadow: '0 1px 0 rgba(255,255,255,0.4)',
        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.15), 0 1px 1px rgba(255,255,255,0.3)',
      }}>{title.replace('The ', '')}</div>
    );
  }

  if (modStyle === 'split') {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <span style={{ fontFamily: 'Georgia, serif', fontSize: '31.3cqw', fontWeight: 900, color: COLORS.ink, lineHeight: 1, textShadow: EMBOSS }}>A</span>
          <span style={{ width: '8.9cqw', flexShrink: 0 }} />
          <span style={{ fontFamily: 'Georgia, serif', fontSize: '31.3cqw', fontWeight: 900, color: COLORS.ink, lineHeight: 1, textShadow: EMBOSS }}>B</span>
          <span style={{
            position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%) rotate(-6deg)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.15,
            color: '#fff', background: COLORS.redPen,
            padding: '2.4cqw 4.7cqw', borderRadius: 6, letterSpacing: '0.03em',
            boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.35), inset 0 -2px 3px rgba(0,0,0,0.25), 0 4px 10px rgba(0,0,0,0.3)',
            zIndex: 2,
          }}>
            <span style={{ fontSize: '7.1cqw', fontWeight: 900 }}>VS</span>
            <span style={{ fontSize: '2.9cqw', fontWeight: 800, letterSpacing: '0.14em', marginTop: 2 }}>ARENA</span>
          </span>
        </div>
      </div>
    );
  }

  return <h3 style={{ fontFamily: 'Georgia, serif', fontSize: '5.8cqw', fontWeight: 900, color: COLORS.ink, margin: 0 }}>{title}</h3>;
};

const ModuleCard = ({ module, index, onClick }) => {
  const [hovered, setHovered] = useState(false);
  const { title, cta, sheet, style: modStyle } = module;
  const tilt = HOVER_TILTS[index % HOVER_TILTS.length];

  if (modStyle === 'formal') {
    return (
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={onClick}
        style={{ cursor: 'pointer', position: 'relative', aspectRatio: MODULE_ASPECT, containerType: 'inline-size', transition: T.trans, transform: hovered ? tilt : 'none' }}
      >
        <img src={sheet} alt="" loading="lazy" decoding="async" draggable={false} style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain',
          pointerEvents: 'none', userSelect: 'none',
          filter: hovered ? 'drop-shadow(0 20px 36px rgba(0,0,0,0.22))' : 'drop-shadow(0 8px 18px rgba(0,0,0,0.14))',
          transition: T.trans,
        }} />
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 18%' }}>
          <span style={{
            fontFamily: 'Georgia, serif', fontSize: '6.3cqw', fontWeight: 800, color: COLORS.ink,
            textAlign: 'center', textShadow: EMBOSS, transform: 'translateY(10%)',
          }}>
            {title}
          </span>
        </div>
        <div style={{ position: 'absolute', left: '11%', right: '11%', bottom: '8%' }}>
          <div style={{
            height: 1, marginBottom: '2.1cqw',
            background: 'linear-gradient(to right, rgba(45,32,8,0.32), transparent 55%)',
          }} />
          <span style={{ fontSize: '5cqw', fontWeight: 700, color: COLORS.purple, fontFamily: T.font.script, display: 'flex', alignItems: 'center', gap: '1.6cqw' }}>
            {cta} <ArrowRight size={15} />
          </span>
        </div>
      </div>
    );
  }

  if (modStyle === 'folder') {
    return (
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={onClick}
        style={{ cursor: 'pointer', position: 'relative', aspectRatio: MODULE_ASPECT, containerType: 'inline-size', transition: T.trans, transform: hovered ? tilt : 'none' }}
      >
        {/* Back two sheets — angled and offset so the stack reads as "a bunch of files".
            Nudged up slightly (translateY reduced) so the bottom sheet doesn't crowd
            whatever sits below/beside this tile in the grid. */}
        <img src={sheet} alt="" loading="lazy" decoding="async" draggable={false} style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain',
          pointerEvents: 'none', userSelect: 'none',
          transform: 'rotate(-6deg) translate(3%, 1.8%)',
          filter: 'brightness(0.92) drop-shadow(0 5px 8px rgba(0,0,0,0.16))',
        }} />
        <img src={sheet} alt="" loading="lazy" decoding="async" draggable={false} style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain',
          pointerEvents: 'none', userSelect: 'none',
          transform: 'rotate(4deg) translate(-2.5%, 1.2%)',
          filter: 'brightness(0.96) drop-shadow(0 7px 12px rgba(0,0,0,0.14))',
        }} />
        {/* Top sheet — holds the real content */}
        <img src={sheet} alt="" loading="lazy" decoding="async" draggable={false} style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain',
          pointerEvents: 'none', userSelect: 'none',
          filter: hovered ? 'drop-shadow(0 20px 36px rgba(0,0,0,0.22))' : 'drop-shadow(0 10px 18px rgba(0,0,0,0.18))',
          transition: T.trans,
        }} />
        <div style={{ position: 'absolute', inset: '14% 14% 8%', display: 'flex', flexDirection: 'column' }}>
          <ModuleTitle module={module} />
          <ModuleCardFooter cta={cta} />
        </div>
      </div>
    );
  }

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
      style={{ cursor: 'pointer', position: 'relative', aspectRatio: MODULE_ASPECT, containerType: 'inline-size', transition: T.trans, transform: hovered ? tilt : 'none' }}
    >
      <img src={sheet} alt="" loading="lazy" decoding="async" draggable={false} style={{
        position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain',
        pointerEvents: 'none', userSelect: 'none',
        filter: hovered ? 'drop-shadow(0 20px 36px rgba(0,0,0,0.22))' : 'drop-shadow(0 8px 18px rgba(0,0,0,0.14))',
        transition: T.trans,
      }} />
      <div style={{ position: 'absolute', inset: '14% 14% 8%', display: 'flex', flexDirection: 'column' }}>
        <ModuleTitle module={module} />
        <ModuleCardFooter cta={cta} />
      </div>
    </div>
  );
};

/* ─── PINNED NOTE — hover shadow now REDUCES (half the base blur/spread/
   opacity) instead of increasing. The front/back flip is already the
   hover animation; rotation is unchanged. ─── */
const PinnedNote = ({ fw, index }) => {
  const [hovered, setHovered] = useState(false);
  const rotations = [-4, 3, -2, 5, -3, 2, -5, 4];
  const rot = rotations[index % rotations.length];
  const pinColors = [COLORS.purple, '#dc2626', '#0891b2', '#ea580c'];
  const pinColor = pinColors[index % pinColors.length];
  const labelColor = COLORS.purple;
  const bodyColor = 'rgba(45,32,8,0.7)';
  const dividerColor = 'rgba(45,32,8,0.1)';
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: 168, height: 148, position: 'relative', flexShrink: 0,
        transform: `rotate(${rot}deg)`,
        transition: 'transform 0.2s ease',
      }}
    >
      <div style={{
        position: 'absolute', top: -7, left: '50%', transform: 'translateX(-50%)',
        width: 13, height: 13, borderRadius: '50%', zIndex: 3,
        background: `radial-gradient(circle at 35% 35%, ${pinColor}, ${pinColor}bb)`,
        boxShadow: '0 2px 4px rgba(0,0,0,0.45)',
        border: '1px solid rgba(0,0,0,0.2)',
      }} />
      <div style={{
        width: '100%', height: '100%', borderRadius: 3, position: 'relative', overflow: 'hidden',
        background: '#fefce8',
        border: '1px solid rgba(45,32,8,0.12)',
        boxShadow: hovered ? '0 2px 5px rgba(0,0,0,0.11)' : '0 4px 10px rgba(0,0,0,0.22)',
        transition: 'box-shadow 0.2s ease',
      }}>
        <div style={{
          position: 'absolute', inset: 0, padding: '18px 14px 14px',
          opacity: hovered ? 0 : 1, transition: 'opacity 0.18s ease',
          pointerEvents: hovered ? 'none' : 'auto',
        }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: labelColor, marginBottom: 6, fontFamily: T.font.serif }}>
            {fw.name}
          </div>
          <div style={{ height: 1, background: dividerColor, marginBottom: 8 }} />
          <p style={{ fontSize: 11, color: bodyColor, lineHeight: 1.5, margin: 0, fontFamily: T.font.mono }}>
            {fw.desc}
          </p>
        </div>
        <div style={{
          position: 'absolute', inset: 0, padding: '18px 14px 14px',
          background: '#fefce8',
          opacity: hovered ? 1 : 0, transition: 'opacity 0.18s ease 0.05s',
          pointerEvents: hovered ? 'auto' : 'none',
        }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: labelColor, marginBottom: 6, fontFamily: T.font.serif }}>
            In Action
          </div>
          <div style={{ height: 1, background: dividerColor, marginBottom: 8 }} />
          <p style={{ fontSize: 11, color: bodyColor, lineHeight: 1.5, margin: 0, fontFamily: T.font.mono, fontStyle: 'italic' }}>
            &ldquo;{fw.example}&rdquo;
          </p>
        </div>
      </div>
    </div>
  );
};

const HowStep = ({ step }) => {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        flex: 1, minWidth: 220, padding: '24px 20px',
        background: 'rgba(254,252,232,0.6)',
        border: '1.5px solid rgba(45,32,8,0.08)',
        borderRadius: T.radius.lg, transition: T.trans,
        transform: hov ? 'translateY(-4px)' : 'none',
        boxShadow: hov ? '0 12px 32px rgba(0,0,0,0.08)' : 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span style={{
          fontSize: 28, fontWeight: 900, color: COLORS.purple, fontFamily: T.font.mono, lineHeight: 1,
        }}>{step.num}</span>
        <span style={{
          fontSize: 12, fontWeight: 800,
          color: COLORS.ink, fontFamily: T.font.mono,
          textDecoration: hov ? 'line-through' : 'none',
          textDecorationColor: COLORS.redPen, textDecorationThickness: '2px',
          transition: 'text-decoration 0.3s',
        }}>{step.title}</span>
      </div>
      <p style={{
        fontSize: 13, color: 'rgba(45,32,8,0.7)',
        lineHeight: 1.65, margin: 0,
      }}>{step.desc}</p>
    </div>
  );
};

const SketchyButton = ({ children, onClick }) => {
  const [hov, setHov] = useState(false);
  const accent = COLORS.purple;
  const textLen = typeof children === 'string' ? children.length : 16;
  const BW = Math.max(240, textLen * 11.5 + 40);
  const BH = 52;
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      onClick={onClick}
      style={{
        display: 'inline-block', cursor: 'pointer', transition: T.trans,
        transform: hov ? 'translateY(-3px) rotate(-0.4deg)' : 'rotate(-0.4deg)',
      }}
    >
      <svg width={BW} height={BH} viewBox={`0 0 ${BW} ${BH}`}>
        <path d={sk(4, 4, BW - 8, BH - 8, 30)} stroke={accent}
          fill={hov ? 'rgba(124,58,237,0.08)' : 'none'}
          strokeWidth={1.6} opacity={0.9}
          style={{ filter: hov ? `drop-shadow(0 4px 12px ${accent}50)` : 'none', transition: 'all 0.25s ease' }} />
        <text x={BW / 2} y={BH / 2 + 7} textAnchor="middle" fontSize="20" fontWeight="700"
          fill={accent} letterSpacing="normal" fontFamily={T.font.script}>
          {children}
        </text>
        {hov && (
          <path d={`M8,${BH - 4} Q${BW / 2},${BH - 7} ${BW - 8},${BH - 4}`} stroke={accent} strokeWidth="1.2" fill="none" strokeLinecap="round" opacity="0.5">
            <animate attributeName="stroke-dasharray" from="0,400" to="300,0" dur="0.4s" fill="freeze" />
          </path>
        )}
      </svg>
    </div>
  );
};

const CopywritingSkeleton = () => {
  const paper = COLORS.paper;
  const pen = '#3d2e08';
  const faint = 'rgba(61,46,8,0.08)';
  return (
    <div style={{
      minHeight: '100vh', backgroundColor: paper,
      backgroundImage: 'radial-gradient(circle, rgba(0,0,0,0.045) 1px, transparent 1px)',
      backgroundSize: '22px 22px',
    }}>
      <style>{`
        @keyframes penWobble{0%,100%{transform:rotate(-2deg)}50%{transform:rotate(2deg)}}
        @keyframes inkFade{0%{opacity:0;transform:translateY(6px)}100%{opacity:1;transform:translateY(0)}}
      `}</style>
      <div className="rail-clearance" style={{
        minHeight: '100vh',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      }}>
      <div style={{ animation: 'inkFade 0.5s ease-out both', textAlign: 'center' }}>
        <svg width="120" height="120" viewBox="0 0 120 120" style={{ display: 'block', margin: '0 auto 16px' }}>
          <path d="M12,14 Q60,10 108,13 Q112,60 109,106 Q60,110 11,107 Q8,60 12,14 Z"
            fill="rgba(61,46,8,0.04)"
            stroke={pen} strokeWidth="1.2" strokeLinejoin="round" opacity="0.5" />
          {[38, 52, 66, 80].map(y => <line key={y} x1="22" y1={y} x2="98" y2={y} stroke={faint} strokeWidth="0.6" />)}
          <line x1="34" y1="16" x2="34" y2="104" stroke="rgba(220,38,38,0.15)" strokeWidth="0.8" />
          <g style={{ animation: 'penWobble 2.5s ease-in-out infinite', transformOrigin: '80px 30px' }}>
            <rect x="74" y="10" width="12" height="52" rx="1.5" fill="#f59e0b" stroke={pen} strokeWidth="1" />
            <rect x="75" y="8" width="10" height="5" rx="1" fill="#fca5a5" stroke={pen} strokeWidth="0.8" />
            <rect x="74" y="13" width="12" height="2.5" fill="#d4c9a8" />
            <path d="M74,62 L86,62 L80,72 Z" fill="#f59e0b" stroke={pen} strokeWidth="0.8" />
            <path d="M75,62 L85,62 L80,70 Z" fill="#d97706" />
            <path d="M78,68 L82,68 L80,72 Z" fill={pen} />
          </g>
          <text x="18" y="95" fill={COLORS.purple} fontSize="9" fontWeight="800" fontFamily={T.font.serif} opacity="0.8">Copywriting</text>
          <text x="18" y="108" fill="rgba(61,46,8,0.3)" fontSize="7" fontFamily={T.font.mono}>loading skill...</text>
        </svg>
        <div style={{
          fontSize: 12, fontWeight: 700,
          color: 'rgba(61,46,8,0.5)',
          fontFamily: T.font.mono, letterSpacing: '0.04em',
        }}>
          loading copywriting
        </div>
      </div>
      </div>
    </div>
  );
};

const CopywritingLanding = () => {
  usePageTitle('Copywriting | Castle Atlas');
  // Fixed light treatment (COLORS.paper is always '#fefce8', regardless of
  // the global theme toggle — confirmed via this file's own colors, no
  // isDark state exists here at all), so the floating rail should read
  // light while this page is mounted, independent of the user's global
  // dark-mode setting.
  useForceSurface('light');
  const navigate = useNavigate();
  const { completedExercises = [], loading: progressLoading } = useProgress();
  // Single width tracker, two derived breakpoints — was a lone isMobile
  // flag at 640px doing double duty for everything on the page. The hero
  // needs its own, wider cutoff: below it drops to one column, above it
  // the text and HeroDeskVisual sit in a real two-track grid (see the c1
  // section) so neither can land on top of the other at in-between
  // widths, which is what was reading as the header "converging."
  const [viewportWidth, setViewportWidth] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth : 1280
  ));

  useEffect(() => {
    const r = () => setViewportWidth(window.innerWidth);
    r();
    window.addEventListener('resize', r);
    return () => window.removeEventListener('resize', r);
  }, []);

  const isMobile = viewportWidth < 640;
  const isHeroStacked = viewportWidth < 900;

  if (progressLoading) return <CopywritingSkeleton />;

  const paper = COLORS.paper;
  const ink = COLORS.ink;
  const muted = 'rgba(45,32,8,0.55)';
  const dotGrid = 'radial-gradient(circle, rgba(0,0,0,0.045) 1px, transparent 1px)';

  // Progress rows are user_progress rows tagged skill_id='copywriting' with the
  // exercise id in exercise_key (written by POST /api/copywriting/score). Only
  // keys that still exist in the data file count, so retired exercises can't
  // push the ring past 100%.
  const doneKeys = new Set(
    completedExercises
      .filter(e => e.skill_id === 'copywriting' && e.exercise_key)
      .map(e => e.exercise_key)
      .filter(key => ALL_COPYWRITING_EXERCISES.some(ex => ex.id === key)),
  );
  const copyProgress = doneKeys.size;
  const totalExercises = ALL_COPYWRITING_EXERCISES.length;
  const progressPct = Math.round((copyProgress / totalExercises) * 100);
  const hasStarted = copyProgress > 0;

  const getModuleDone = (slug) =>
    getExercisesByModule(slug).filter(ex => doneKeys.has(ex.id)).length;

  const radarData = RADAR_AXES.map(skillName => {
    const mod = MODULES.find(m => MODULE_SKILL_MAP[m.slug] === skillName);
    if (!mod) return { skill: skillName, value: 0 };
    const total = getExercisesByModule(mod.slug).length;
    return { skill: skillName, value: total ? Math.round((getModuleDone(mod.slug) / total) * 100) : 0 };
  });

  return (
    <div style={{
      minHeight: '100vh', backgroundColor: paper, backgroundImage: dotGrid,
      backgroundSize: '22px 22px',
      fontFamily: T.font.sans,
    }}>
      <style>{`
        @keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
        @keyframes livingPulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:0.82;transform:scale(1.035)}}
  @keyframes livingPulseOpacity {
    0%, 100% { opacity: 0.05; }
    50% { opacity: 0.15; }
  }
        .c1{animation:fadeUp 0.5s cubic-bezier(0.22,1,0.36,1) both}
        .c2{animation:fadeUp 0.5s cubic-bezier(0.22,1,0.36,1) 0.08s both}
        .c3{animation:fadeUp 0.5s cubic-bezier(0.22,1,0.36,1) 0.16s both}
        .c4{animation:fadeUp 0.5s cubic-bezier(0.22,1,0.36,1) 0.24s both}
        .c5{animation:fadeUp 0.5s cubic-bezier(0.22,1,0.36,1) 0.32s both}
        .c6{animation:fadeUp 0.5s cubic-bezier(0.22,1,0.36,1) 0.40s both}
      `}</style>
      <div className="rail-clearance" style={{ padding: '24px 24px 80px' }}>
        {/* Real two-column grid instead of a free-floating position:absolute
           image. The old version had one hard cutoff (isMobile, 640px) that
           switched between "stacked" and "desktop" — but the desktop image
           was sized off 46vw with no column of its own, so from 640px up to
           ~1400px wide it had no reserved space and sat directly on top of
           the text. Giving each side a real grid track means the text can
           never be overlapped at any width; only the image (in its own
           track, zIndex below the text, pointer-events none) is allowed to
           bleed past ITS edge, toward the top-right where there's nothing
           under it to collide with. */}
        <section className="c1" style={{
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: isHeroStacked ? '1fr' : 'minmax(320px, 560px) minmax(280px, 1fr)',
          alignItems: 'center',
          columnGap: 24,
          padding: '8px 0 32px',
        }}>
          <div style={{ position: 'relative', zIndex: 2 }}>
            <div style={{
              fontSize: 12, fontFamily: T.font.mono, color: muted,
              marginBottom: 20, display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <span>~</span><span style={{ color: COLORS.purple }}>/skills/copywriting</span>
            </div>
            <HeroCrossout />
            <p style={{
              fontSize: 15, fontWeight: 700, color: COLORS.purple,
              margin: '14px 0 10px', fontFamily: T.font.serif,
            }}>
              Five modules. Real briefs. Scored by AI.
            </p>
            <p style={{
              fontSize: 14, color: muted, lineHeight: 1.65,
              maxWidth: 480, marginBottom: 10,
            }}>
              The Copy Lab is a chaotic creative workspace where you write, cross out, revise, and rewrite until your copy converts. No clean SaaS. Just raw creative reps.
            </p>
            <p style={{
              fontSize: 13, color: muted, lineHeight: 1.6,
              maxWidth: 480, marginBottom: 24,
            }}>
              {totalExercises} exercises across headlines, ad copy, swipe files, client briefs, and A/B testing — each one a real brief, scored and broken down by AI the moment you submit.
            </p>
            {hasStarted && (
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 24 }}>
                {Array.from({ length: totalExercises }).map((_, i) => (
                  <div key={i} style={{
                    width: 7, height: 7, borderRadius: '50%',
                    background: i < copyProgress ? COLORS.purple : 'transparent',
                    border: `1.5px solid ${i < copyProgress ? COLORS.purple : 'rgba(45,32,8,0.2)'}`,
                    boxShadow: i < copyProgress ? '0 0 5px rgba(124,58,237,0.5)' : 'none',
                    transition: 'all 0.3s ease',
                  }} />
                ))}
                <span style={{ fontSize: 10, color: muted, marginLeft: 6, alignSelf: 'center' }}>
                  {copyProgress}/{totalExercises} reps
                </span>
              </div>
            )}
            <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
              <SketchyButton onClick={() => navigate(`/skills/copywriting/${SLUG_MAP['01']}`)}>
                {hasStarted ? `→ Continue · ${progressPct}%` : '→ Start Writing'}
              </SketchyButton>
            </div>
          </div>

          {/* Image track. On desktop this cell reserves real grid space
             (min 280px), and the visual itself is sized wider than that
             cell and pushed upward with negative margins — the overflow
             direction is right and up, away from the text column, so it
             can bleed without ever crossing into it. Sized down from 148%
             to 126% (15% smaller) and pushed up further (-60 -> -90) now
             that the page's own top padding is much smaller (60px -> 24px
             above), so the crumpled paper at the top of the photo reaches
             into that margin instead of sitting inside the section. When
             stacked (single column, under 900px), it drops to a plain
             width:100% of that column with no separate cap. Tune the
             126%/-6%/-90px trio together on a real screen — the exact
             bleed depends on the real image's aspect ratio and the actual
             rendered column width, neither of which I can see from here. */}
          <div style={{
            position: 'relative', zIndex: 1, pointerEvents: 'none',
            width: '100%', minWidth: 0,
            marginTop: isHeroStacked ? 32 : 0,
            display: 'flex', justifyContent: isHeroStacked ? 'center' : 'flex-start',
          }}>
            <div style={{
              width: isHeroStacked ? '100%' : '126%',
              marginTop: isHeroStacked ? 0 : -90,
              marginLeft: isHeroStacked ? 0 : '-6%',
            }}>
              <HeroDeskVisual />
            </div>
          </div>
        </section>

        <section className="c2" style={{
          borderTop: `1px solid ${COLORS.purple}40`,
          borderBottom: `1px solid ${COLORS.purple}40`,
          padding: '30px 4px', marginBottom: 40,
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 28,
          }}>
            {[
              { icon: PenTool, title: 'Real Briefs', desc: 'Not fake exercises. Real ad scenarios, client briefs, email campaigns.' },
              { icon: Sparkles, title: 'AI Scoring', desc: 'Not just "good job." Breakdown of what worked, what did not, and why. Instantly.' },
              { icon: BookOpen, title: 'Proven Frameworks', desc: 'The ones that made billions. Ogilvy. Sugarman. Halbert. Bly. All here.' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <Icon size={17} color={COLORS.purple} />
                  <h3 style={{ fontSize: 15, fontWeight: 900, color: COLORS.ink, margin: 0 }}>{title}</h3>
                </div>
                <p style={{
                  fontSize: 12, color: muted,
                  lineHeight: 1.6, margin: 0,
                }}>{desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="modules" className="c3" style={{ marginBottom: 48 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <Layers size={14} color={COLORS.purple} />
            <span style={{
              fontSize: 11, fontWeight: 800, color: muted,
              textTransform: 'uppercase', letterSpacing: '0.08em',
            }}>The Modules</span>
          </div>
          {/* Flexbox instead of CSS Grid here on purpose. The old version
             was `display:grid` with `repeat(auto-fit, minmax(...))` plus a
             separate JS ResizeObserver that re-measured the container and
             replicated auto-fit's own column-count math, so it could hand
             the trailing row (whichever tiles didn't fill a full row) an
             explicit grid-column-start to center them. Centering a partial
             last row is something CSS Grid genuinely can't do on its own —
             but that meant two independent systems (the browser's real
             auto-fit resolution, and the JS's guess at the same number)
             had to agree on the column count on every resize, in every
             browser, forever. Whenever they disagreed by even one column,
             a tile got a grid-column-start that didn't exist in the real
             grid — the browser then silently created an extra implicit
             column with ~0 width to satisfy it, which is exactly the
             "Swipe File shrank to an icon" / "the radar chart vanished"
             bug. Flexbox with wrap + justify-content:center centers a
             partial last row natively, with no measuring and only one
             source of truth, at the cost of cards no longer stretching
             edge-to-edge on very wide screens (they cap at
             MODULE_GRID_MAX instead). */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: MODULE_GRID_GAP,
            }}
          >
            {MODULES.map((mod, i) => (
              <div key={mod.num} style={{ flex: `1 1 ${MODULE_GRID_MIN}px`, maxWidth: MODULE_GRID_MAX }}>
                <ModuleCard
                  module={mod}
                  index={i}
                  onClick={() => navigate(`/skills/copywriting/${mod.slug}`)}
                />
              </div>
            ))}
            <div style={{ flex: `1 1 ${MODULE_GRID_MIN}px`, maxWidth: MODULE_GRID_MAX }}>
              <RadarTile radarData={radarData} isGhost={!hasStarted} />
            </div>
          </div>
        </section>

        <section className="c4" style={{ marginBottom: 48 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <Zap size={14} color={COLORS.purple} />
            <span style={{
              fontSize: 11, fontWeight: 800, color: muted,
              textTransform: 'uppercase', letterSpacing: '0.08em',
            }}>How It Works</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            {HOW_STEPS.map(step => <HowStep key={step.num} step={step} />)}
          </div>
        </section>

        <section className="c5" style={{ marginBottom: 48 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <BookOpen size={14} color={COLORS.purple} />
            <span style={{
              fontSize: 11, fontWeight: 800, color: muted,
              textTransform: 'uppercase', letterSpacing: '0.08em',
            }}>The Frameworks Behind the Copy</span>
          </div>
          <div style={{
            background: 'radial-gradient(ellipse at 30% 20%, #cfa876, #a67c4e 75%)',
            border: '10px solid #8b6339',
            borderRadius: T.radius.lg,
            boxShadow: 'inset 0 2px 14px rgba(0,0,0,0.35)',
            padding: '30px 24px 26px',
            display: 'flex', flexWrap: 'wrap', gap: '22px 18px',
            justifyContent: 'center',
          }}>
            {FRAMEWORKS.map((fw, i) => (
              <PinnedNote key={fw.name} fw={fw} index={i} />
            ))}
          </div>
        </section>

        <section className="c6" style={{
          background: COLORS.band.bg,
          border: `1px solid ${COLORS.band.border}`,
          borderRadius: T.radius.xl,
          padding: isMobile ? '36px 24px' : '40px 48px',
          marginBottom: 40,
          display: 'flex', flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'center', justifyContent: 'center', gap: isMobile ? 28 : 52,
        }}>
          <ProgressCard
            pct={progressPct} size={isMobile ? 70 : 90}
            copyProgress={copyProgress} totalExercises={totalExercises}
          />
          {!isMobile && <div style={{ width: 1, alignSelf: 'stretch', background: COLORS.band.border }} />}
          <div style={{ textAlign: isMobile ? 'center' : 'left' }}>
            <p style={{
              fontSize: 'clamp(1.2rem, 2.6vw, 1.7rem)', fontWeight: 900,
              color: ink, lineHeight: 1.2, margin: '0 0 4px', fontFamily: T.font.serif,
            }}>
              Stop writing{' '}
              {['bad', 'mediocre', 'okay'].map(w => (
                <span key={w} style={{
                  textDecoration: 'line-through', textDecorationColor: COLORS.redPen,
                  textDecorationThickness: '3px', color: muted,
                }}>{w} </span>
              ))}
              copy.
            </p>
            <p style={{
              fontSize: 'clamp(1.2rem, 2.6vw, 1.7rem)', fontWeight: 900,
              color: COLORS.purple, lineHeight: 1.2, margin: '0 0 20px', fontFamily: T.font.serif,
            }}>Start writing copy that converts.</p>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'center' : 'flex-start', gap: 10 }}>
              <SketchyButton onClick={() => navigate(`/skills/copywriting/${SLUG_MAP['01']}`)}>
                {hasStarted ? `→ Continue · ${progressPct}%` : '→ Start the first module'}
              </SketchyButton>
              <p style={{
                fontSize: 10, color: muted, fontFamily: T.font.mono,
                letterSpacing: '0.02em', margin: 0,
              }}>
                Free to start · No credit card · Judgment from an AI, not a human
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default CopywritingLanding;